import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, FileText, Printer, FilePen, FileX, Send } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api';
import { SigPad } from '../components/timeclock/SigPad';
import { SignedFiles } from '../components/timeclock/SignedFiles';
import { STAFF_DOCS, LINE_DOCS, DISCIPLINES, specFor, weeklyLines, disciplineOf } from '../components/timeclock/staffDocSpecs';
import { TimesheetLines, drop } from '../components/timeclock/TimesheetLines';
import { ContractorInvoices } from '../components/timeclock/ContractorInvoices';
import { buildStaffPdf } from '../utils/staffDocs';
import { addDays, dayKey, fetchEntries, fmtDate, fmtTime, setAgencyTz, summarize, weekStart } from '../utils/timeclock';

const MODES = [
  ['prefill', 'Editable (pre-filled)', FilePen, 'bg-[#d4af37] text-gray-900'],
  ['blank', 'Blank fillable form', FileX, 'border border-gray-400 bg-white'],
  ['flat', 'PDF copy', Download, 'border border-blue-700 text-blue-800 bg-white'],
  ['print', 'Print', Printer, 'bg-gray-100 border border-gray-300'],
];

const DocPills = ({ isOffice, current, navigate }) => (
  <div className="flex flex-wrap gap-1" data-testid="staff-hub-docs">
    {[...Object.entries(STAFF_DOCS).filter(([, d]) => !d.office || isOffice), ['signed_files', { name: 'Signed files' }], ['invoices', { name: 'Contractor invoices' }]].map(([k, d]) => (
      <button key={k} onClick={() => navigate(`/dashboard/staff-hub/${k}`)} data-testid={`staff-hub-doc-${k}`}
        className={`px-3 py-1.5 rounded-full text-sm border ${k === current ? 'bg-gray-900 text-[#d4af37] border-gray-900' : 'bg-white hover:border-[#d4af37]'} ${['signed_files', 'invoices'].includes(k) ? 'font-semibold' : ''}`}>{d.name}</button>
    ))}
  </div>
);

const Preview = ({ spec }) => (
  <div className="overflow-x-auto border rounded-lg" data-testid="staff-doc-preview">
    <div className="bg-gray-50 px-3 py-2 text-sm flex flex-wrap gap-x-6">{spec.info.map(([k, v]) => <span key={k}><span className="text-gray-500">{k}:</span> <strong>{v || '—'}</strong></span>)}</div>
    {spec.columns && (
      <table className="w-full text-sm">
        <thead className="bg-gray-900 text-[#d4af37] text-left"><tr>{spec.columns.map((c) => <th key={c.h} className="px-2 py-1.5 whitespace-nowrap">{c.h}</th>)}</tr></thead>
        <tbody>
          {spec.rows.map((r, i) => <tr key={i} className={`border-t ${r.shade ? 'bg-amber-50 font-semibold' : ''}`}>{r.map((c, j) => <td key={j} className="px-2 py-1">{c}</td>)}</tr>)}
          {!spec.rows.length && <tr><td colSpan={spec.columns.length} className="px-2 py-4 text-gray-500" data-testid="staff-doc-empty">Nothing recorded for this period. The blank form is still available.</td></tr>}
        </tbody>
      </table>
    )}
    {spec.form && <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 p-3 text-sm">{spec.form.map(([k, v]) => <div key={k}><dt className="text-gray-500">{k}</dt><dd className="font-semibold">{v || '—'}</dd></div>)}</dl>}
    {spec.totals?.length > 0 && <div className="px-3 py-2 border-t text-sm flex flex-wrap gap-x-6 font-semibold" data-testid="staff-doc-totals">{spec.totals.map(([k, v]) => <span key={k}>{k}: {v}</span>)}</div>}
  </div>
);

export default function StaffHubPage() {
  const { doc: docParam } = useParams();
  const navigate = useNavigate();
  const key = STAFF_DOCS[docParam] ? docParam : 'weekly_timesheet';
  const me = JSON.parse(localStorage.getItem('ivory_user') || '{}');
  const [isOffice, setIsOffice] = useState(false);
  const [staff, setStaff] = useState([]);
  const [who, setWho] = useState(me.id);
  const [start, setStart] = useState(weekStart());
  const [days, setDays] = useState(14);
  const [tsLen, setTsLen] = useState('week');
  const [entries, setEntries] = useState([]);
  const [visits, setVisits] = useState([]);
  const [patients, setPatients] = useState([]);
  const [lines, setLines] = useState([]);
  const [showNames, setShowNames] = useState(false);
  const [fillEmp, setFillEmp] = useState(false);
  const [pickEmp, setPickEmp] = useState(false);
  const [jobTitle, setJobTitle] = useState('');
  const [requests, setRequests] = useState([]);
  const [entryId, setEntryId] = useState('');
  const [rate, setRate] = useState('');
  const [sigs, setSigs] = useState({ employee: null, supervisor: null });
  const [busy, setBusy] = useState(false);
  const cfg = STAFF_DOCS[key];
  const lineDoc = LINE_DOCS.includes(key);
  const tsDays = tsLen === 'week' ? 7 : tsLen === 'biweek' ? 14 : new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
  const span = cfg.range === 'period' ? days : lineDoc ? tsDays : 7;

  useEffect(() => {
    api.get('/timeclock/status').then((r) => {
      setAgencyTz(r.data.timezone);
      setIsOffice(r.data.isOffice);
      if (r.data.isOffice) api.get('/timeclock/staff').then((s) => setStaff(s.data.staff));
      // office sees every patient; other staff only the patients assigned to them
      const names = (list) => setPatients([...new Set(list.filter(Boolean))].sort());
      if (r.data.isOffice) api.get('/patients').then((x) => names((x.data.patients || []).filter((p) => p.status !== 'Discharged').map((p) => `${p.firstName || ''} ${p.lastName || ''}`.trim())));
      else api.get('/caseload/mine').then((x) => names((x.data.patients || []).map((p) => p.name)));
    }).catch(() => {});
  }, []);
  const load = useCallback(async () => {
    try {
      const allStaff = key === 'pay_period_summary';
      setEntries(await fetchEntries(start, addDays(start, span), allStaff ? undefined : who));
      if (key === 'visit_verification' || LINE_DOCS.includes(key)) setVisits((await api.get('/timeclock/visit-log', { params: { start: dayKey(start), days: span, staffId: who } })).data.visits);
      if (key === 'time_correction') setRequests((await api.get('/timeclock/requests', { params: { status: 'All' } })).data.requests);
    } catch { toast.error('Could not load Time Clock data'); }
  }, [key, start, span, who]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSigs({ employee: null, supervisor: null }); }, [key]);

  const emp = who === me.id ? { name: `${me.firstName || ''} ${me.lastName || ''}`.trim(), role: me.role } : staff.find((s) => s.id === who);
  const sumDays = lineDoc ? tsDays : 7;
  const summary = useMemo(() => { // overtime is figured per 7-day block
    const all = Array.from({ length: sumDays }, (_, i) => addDays(start, i));
    const ws = [];
    for (let i = 0; i < all.length; i += 7) ws.push(summarize(entries, all.slice(i, i + 7)));
    const total = Object.fromEntries(Object.keys(ws[0].total).map((k) => [k, k === 'open' ? ws.some((w) => w.total.open) : ws.reduce((t, w) => t + w.total[k], 0)]));
    return { perDay: ws.flatMap((w) => w.perDay), total };
  }, [entries, start, sumDays]);
  const refill = useCallback(() => setLines(weeklyLines(summary, visits, emp, showNames)), [summary, visits, emp?.name, emp?.role, showNames]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (LINE_DOCS.includes(key)) refill(); }, [key, refill]);
  const entry = entries.find((e) => e.id === entryId);
  const request = requests.find((r) => r.entryId === entryId);
  const spec = specFor(key, { emp, start, days, periodDays: sumDays, tsLen, entries, summary, visits, staff, entry, request, rate, sigs, patients, lines, showNames, jobTitle, fillEmp: fillEmp || !lineDoc });
  const us = (d) => `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  const dateOptions = [...new Set([...Array.from({ length: span }, (_, i) => us(addDays(start, i))), us(new Date())])];
  const make = async (mode) => {
    setBusy(true);
    try { await buildStaffPdf({ ...spec, mode, dateOptions }); } catch (e) { console.error(e); toast.error('Could not create the PDF'); }
    setBusy(false);
  };
  const submit = async () => {
    if (!sigs.employee) { toast.error('Sign in the first signature box before submitting'); return; }
    setBusy(true);
    try {
      const clean = specFor(key, { emp, start, days, periodDays: sumDays, tsLen, entries, summary, visits, staff, entry, request, rate, patients, lines, showNames, jobTitle, fillEmp: fillEmp || !lineDoc, sigs: { employee: sigs.employee, supervisor: null } });
      const blob = await buildStaffPdf({ ...clean, mode: 'submit' });
      const fd = new FormData();
      fd.append('file', blob, clean.filename);
      fd.append('docType', key);
      fd.append('periodStart', dayKey(start));
      fd.append('periodEnd', dayKey(addDays(start, span - 1)));
      if (who && who !== me.id && cfg.range !== 'period') fd.append('staffId', who);
      await api.post('/staff-files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Signed copy saved to the employee file and sent to the office for approval');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit'); }
    setBusy(false);
  };
  if (docParam === 'invoices') {
    return (
      <DashboardLayout>
        <div className="space-y-4 max-w-[1500px]" data-testid="staff-hub-page">
          <div><p className="text-xs font-bold uppercase tracking-wide text-[#b8860b]">Staff Hub</p><h1 className="text-2xl font-bold text-gray-900">Contractor invoices</h1>
            <p className="text-sm text-gray-600">{isOffice ? 'Upload a contractor\'s invoice (PDF or photo). It is read into the app automatically; check it, approve it, then record the payment.' : 'Upload your invoice (PDF or photo) and follow it through approval and payment.'}</p></div>
          <DocPills isOffice={isOffice} current="invoices" navigate={navigate} />
          <ContractorInvoices />
        </div>
      </DashboardLayout>
    );
  }
  if (docParam === 'signed_files') {
    return (
      <DashboardLayout>
        <div className="space-y-4 max-w-6xl" data-testid="staff-hub-page">
          <div><p className="text-xs font-bold uppercase tracking-wide text-[#b8860b]">Staff Hub</p><h1 className="text-2xl font-bold text-gray-900">Signed files</h1>
            <p className="text-sm text-gray-600">{isOffice ? 'Every employee\'s signed documents. Approve to countersign, or return with a note.' : 'Your signed documents and whether the office approved them.'}</p></div>
          <DocPills isOffice={isOffice} current="signed_files" navigate={navigate} />
          <SignedFiles key={String(isOffice)} isOffice={isOffice} staff={staff} />
        </div>
      </DashboardLayout>
    );
  }
  if (cfg.office && !isOffice) {
    return <DashboardLayout><p className="p-6 text-gray-600" data-testid="staff-hub-office-only">The Pay Period Summary is for the office.</p></DashboardLayout>;
  }
  return (
    <DashboardLayout>
      <div className="space-y-4 max-w-[1500px]" data-testid="staff-hub-page">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-[#b8860b]">Staff Hub</p>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><FileText className="w-6 h-6" />{cfg.name}</h1>
          <p className="text-sm text-gray-600">Filled from the Time Clock. Download it editable, as a blank fillable form or a finished PDF copy, or print it.</p>
        </div>
        <DocPills isOffice={isOffice} current={key} navigate={navigate} />
        <div className="bg-white border rounded-xl p-4 flex flex-wrap items-end gap-3">
          {lineDoc && !pickEmp && !(fillEmp && !isOffice) && (
            <button onClick={() => { if (isOffice) setPickEmp(true); else setFillEmp(true); }} className="px-3 py-2 rounded border bg-white text-sm" data-testid="staff-hub-choose-employee">
              {isOffice ? 'Choose employee' : 'Fill in my name'}</button>
          )}
          {isOffice && cfg.range !== 'period' && (!lineDoc || pickEmp) && (
            <label className="text-sm">Employee<select value={who} onChange={(e) => { setWho(e.target.value); setEntryId(''); setFillEmp(true); }} className="block border rounded px-2 py-1.5" data-testid="staff-hub-employee">
              <option value={me.id}>Me ({`${me.firstName || ''} ${me.lastName || ''}`.trim()})</option>
              {staff.filter((s) => s.id !== me.id).map((s) => <option key={s.id} value={s.id}>{s.name} – {s.role}</option>)}
            </select></label>
          )}
          {lineDoc && (
            <label className="text-sm">{key === 'contractor_timesheet' ? 'Discipline' : 'Job title / discipline'}<select value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}
              className={`block w-44 ${drop}`} data-testid="staff-hub-job-title"><option value="" />{DISCIPLINES.map((d) => <option key={d}>{d}</option>)}</select></label>
          )}
          {lineDoc && (
            <label className="text-sm">Timesheet covers<select value={tsLen} onChange={(e) => { setTsLen(e.target.value); if (e.target.value === 'month') setStart(new Date(start.getFullYear(), start.getMonth(), 1)); else setStart(weekStart(start)); }}
              className="block border rounded px-2 py-1.5" data-testid="staff-hub-ts-length">
              <option value="week">Weekly (7 days)</option><option value="biweek">Biweekly (14 days)</option><option value="month">Monthly (whole month)</option></select></label>
          )}
          <label className="text-sm">{cfg.range === 'period' ? 'Period starts' : lineDoc && tsLen === 'month' ? 'Month (any day in it)' : 'Week starting (Sunday)'}
            <input type="date" value={dayKey(start)} onChange={(e) => { if (!e.target.value) return; const d = new Date(`${e.target.value}T00:00:00`); setStart(cfg.range === 'period' ? d : lineDoc && tsLen === 'month' ? new Date(d.getFullYear(), d.getMonth(), 1) : weekStart(d)); }}
              className="block border rounded px-2 py-1.5" data-testid="staff-hub-start" /></label>
          {cfg.range === 'period' && (
            <label className="text-sm">Length<select value={days} onChange={(e) => setDays(Number(e.target.value))} className="block border rounded px-2 py-1.5" data-testid="staff-hub-length">
              <option value={7}>1 week</option><option value={14}>2 weeks</option><option value={28}>4 weeks</option></select></label>
          )}
          {(key === 'mileage_log' || key === 'contractor_timesheet') && <label className="text-sm">Rate per mile ($)<input type="number" step="0.01" min="0" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="e.g. 0.70" className="block w-28 border rounded px-2 py-1.5" data-testid="staff-hub-rate" /></label>}
          {key === 'time_correction' && (
            <label className="text-sm">Clock entry to correct<select value={entryId} onChange={(e) => setEntryId(e.target.value)} className="block border rounded px-2 py-1.5 max-w-xs" data-testid="staff-hub-entry">
              <option value="">— none (blank form) —</option>
              {entries.map((e) => <option key={e.id} value={e.id}>{fmtDate(e.clockIn)} {fmtTime(e.clockIn)}–{e.clockOut ? fmtTime(e.clockOut) : 'missing'} · {e.kind === 'visit' ? e.patientName : 'Shift'}</option>)}
            </select></label>
          )}
        </div>
        {LINE_DOCS.includes(key) && (
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-fit" data-testid="staff-hub-names-toggle">
              <input type="checkbox" checked={showNames} onChange={(e) => setShowNames(e.target.checked)} data-testid="staff-hub-show-names" />
              Fill in patient names from the Time Clock
            </label>
            <label className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-fit" data-testid="staff-hub-emp-toggle">
              <input type="checkbox" checked={fillEmp} onChange={(e) => setFillEmp(e.target.checked)} data-testid="staff-hub-fill-employee" />
              Fill in employee name
            </label>
          </div>
        )}
        <Preview spec={LINE_DOCS.includes(key) ? { ...spec, columns: null } : spec} />
        {LINE_DOCS.includes(key) && (
          <TimesheetLines lines={lines} setLines={setLines} patients={showNames ? patients : []} showNames={showNames} onReset={refill} contractor={key === 'contractor_timesheet'}
            dates={Array.from({ length: span }, (_, i) => dayKey(addDays(start, i)))}
            people={[...new Set([emp?.name, ...staff.map((s) => s.name)].filter(Boolean))]} defaults={{ employee: emp?.name || '', disc: disciplineOf(emp?.role), date: dayKey(start) }} />
        )}
        <div key={key} className="bg-white border rounded-xl p-4 grid md:grid-cols-2 gap-4" data-testid="staff-hub-signatures">
          <SigPad label={who && who !== me.id && cfg.range !== 'period' ? `Employee signature – you are signing on behalf of ${emp?.name || 'the employee'} (optional)` : `${spec.signatures[0].label} signature (sign here to submit, or sign on paper)`} testid="sig-employee" onChange={(v) => setSigs((s) => ({ ...s, employee: v }))} />
          <SigPad label={`${spec.signatures[1].label} signature (optional)`} testid="sig-supervisor" onChange={(v) => setSigs((s) => ({ ...s, supervisor: v }))} />
        </div>
        <div className="flex flex-wrap gap-2" data-testid="staff-hub-actions">
          {MODES.map(([mode, label, Icon, cls]) => (
            <button key={mode} disabled={busy} onClick={() => make(mode)} data-testid={`staff-hub-${mode}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 ${cls}`}><Icon className="w-4 h-4" />{label}</button>
          ))}
          <button disabled={busy} onClick={submit} data-testid="staff-hub-submit"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold bg-green-700 text-white hover:bg-green-800 disabled:opacity-50"><Send className="w-4 h-4" />Sign &amp; submit to office</button>
        </div>
      </div>
    </DashboardLayout>
  );
}
